import { getDeviceType } from '../utils/browser.js';

export function checkURL(rules, urlRulesLogicOperator, attributes) {
    if (!rules || rules.length === 0) {
        return false;
    }

    if (urlRulesLogicOperator === 'OR') {
        return rules.some((rule) => checkRule(rule, attributes));
    } else {
        // AND is default
        return rules.every((rule) => checkRule(rule, attributes));
    }
}

function isEmptyRuleValue(value) {
    if (value === undefined || value === null || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    return false;
}

export function checkRule(rule, attributes) {
    const { criterion, operator } = rule;
    let value = rule.value ?? '';

    let actualValue = getCriterionValue(criterion, value, attributes);

    // A criterion without a value (attributes not loaded, user unknown, field
    // missing) never satisfies a rule that asks for a value. Without this guard
    // `String(undefined)` is compared, so `country contain "de"` would match
    // "undefined". Only an empty rule value matches an empty criterion value.
    if (actualValue === undefined || actualValue === null) {
        if (!isEmptyRuleValue(value)) return false;
        actualValue = '';
    }

    switch (operator) {
        case 'contain':
            if (criterion === 'deviceType') {
                return value.toLowerCase().includes(String(actualValue).toLowerCase());
            }
            return String(actualValue).includes(value);

        case 'notContain':
            if (criterion === 'deviceType') {
                return !value.toLowerCase().includes(String(actualValue).toLowerCase());
            }
            return !String(actualValue).includes(value);

        case 'exact':
            if (criterion === 'customUserAttributes') {
                let customValue = actualValue[value.key];
                if (customValue === undefined || customValue === null) {
                    return isEmptyRuleValue(value.value);
                }
                return String(customValue) === String(value.value);
            }
            return String(actualValue) === String(value);

        case 'notExact':
            if (criterion === 'customUserAttributes') {
                let customValue = actualValue[value.key];
                if (customValue === undefined || customValue === null) {
                    return false;
                }
                return String(customValue) !== String(value.value);
            }
            return String(actualValue) !== String(value);

        case 'regex':
            try {
                return new RegExp(value).test(String(actualValue));
            } catch {
                return false;
            }

        case 'gt':
            return Number(actualValue) > Number(value);

        case 'lt':
            return Number(actualValue) < Number(value);

        case 'gte':
            return Number(actualValue) >= Number(value);

        case 'lte':
            return Number(actualValue) <= Number(value);

        case 'between':
            value = Array.isArray(value) ? value : String(value).split(',').map((v) => v.trim());
            if (!Array.isArray(value) || value.length !== 2) return false;
            return Number(actualValue) >= Number(value[0]) && Number(actualValue) <= Number(value[1]);

        case 'isSet':
            return Object.prototype.hasOwnProperty.call(actualValue, value);

        case 'isNotSet':
            return !Object.prototype.hasOwnProperty.call(actualValue, value);

        default:
            return false;
    }
}

export function getCriterionValue(criterion, value, attributes = {}) {
    switch (criterion) {
        /* ===== PAGE ===== */
        case 'url':
            return window.location.href;

        case 'pageLanguage':
            return document.documentElement.lang;

        case 'pageHostname':
            return window.location.hostname;

        // Same semantics as the reporting segments: referrerHostname is the
        // full host ("www.google.de"), referrerDomain the registrable domain
        // ("google.de")
        case 'referrerHostname':
            return getReferrerHost();

        case 'referrerDomain':
            return getRegistrableDomain(getReferrerHost());

        /* ===== DEVICE ===== */
        case 'deviceType':
            return getDeviceType();

        case 'screenWidth':
            return window.innerWidth;

        /* ===== E-COMMERCE ===== */
        case 'ordersCount':
            return attributes.userAttributes?.ordersCount;

        case 'totalPurchases':
            return attributes.userAttributes?.totalPurchases;

        case 'totalRevenue':
            return attributes.userAttributes?.totalRevenue;

        case 'totalAov':
            return attributes.userAttributes?.totalAov;

        case 'orderItemSkus':
            return attributes.orderAttributes?.orderItemSkus?.join(',') ?? '';

        case 'itemBrands':
            return attributes.orderAttributes?.itemBrands?.join(',') ?? '';

        case 'customerType':
            return attributes.userAttributes?.customerType;

        case 'couponCodes':
            return attributes.orderAttributes?.couponCodes?.join(',') ?? '';

        case 'category':
        case 'category2':
        case 'category3':
        case 'category4':
        case 'category5':
            return attributes.orderAttributes?.[criterion]?.join(',') ?? '';

        /* ===== USER ===== */
        case 'country':
            return attributes.userAttributes?.country;

        case 'language':
            return attributes.userAttributes?.language;

        case 'customUserAttributes':
            return attributes.customUserAttributes ?? {};

        default:
            return undefined;
    }
}

function getReferrerHost() {
    if (!document.referrer) return '';
    try {
        return new URL(document.referrer).hostname;
    } catch {
        return '';
    }
}

// Second-level labels under which the registrable domain has three labels
// (shop.example.co.uk -> example.co.uk)
const SECOND_LEVEL_LABELS = new Set(['co', 'com', 'org', 'net', 'gov', 'edu', 'ac']);

export function getRegistrableDomain(hostname) {
    if (!hostname) return '';
    const labels = hostname.toLowerCase().split('.').filter(Boolean);
    if (labels.length <= 2) return labels.join('.');
    const tld = labels[labels.length - 1];
    const second = labels[labels.length - 2];
    const count = tld.length === 2 && SECOND_LEVEL_LABELS.has(second) ? 3 : 2;
    return labels.slice(-count).join('.');
}
